import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getAllPatients } from "../../../api/apiService";
import Search from "../../../assets/images/comman/search-outline.svg";
import { getAgoTime } from "../Patients/utils";
import {
  ensurePatientHasVisit,
  patientHasNoVisits,
  calculateAge,
  formatPatientName,
  formatDobDisplay,
} from "../../../utils/patientVisitUtils";
import CreateVisitModal from "../../../components/common/CreateVisitModal";

import ToastContainer, {
  useToast,
} from "../../../components/common/ToastContainer";

const DischargeSummary = () => {
  const navigate = useNavigate();
  const { toasts, removeToast, showError } = useToast();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalPatients, setTotalPatients] = useState(0);
  const [navigatingPatientId, setNavigatingPatientId] = useState(null);
  const [showVisitModal, setShowVisitModal] = useState(false);
  const [patientForVisit, setPatientForVisit] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("All");

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    const options = { day: "2-digit", month: "short", year: "numeric" };
    return date.toLocaleDateString("en-GB", options);
  };

  // Helper function to get time ago
  const getTimeAgo = (dateString) => getAgoTime(dateString);

  // Fetch patients data from API
  useEffect(() => {
    const fetchPatients = async () => {
      try {
        setLoading(true);
        const response = await getAllPatients();

        if (response.success) {
          // Transform API data to match component structure
          const transformedPatients = response.data.patients.map((patient) => {
            const rawName =
              patient.name && typeof patient.name === "string" && patient.name.trim() && patient.name.trim() !== "undefined undefined"
                ? patient.name.trim()
                : patient.fullName && typeof patient.fullName === "string" && patient.fullName.trim() && patient.fullName.trim() !== "undefined undefined"
                ? patient.fullName.trim()
                : formatPatientName(patient) || "Patient";
            const fullName = rawName;
            const dobFormatted = formatDobDisplay(patient.dob || patient.dateOfBirth);
            const computedAge = (() => {
              const calc = calculateAge(dobFormatted);
              if (calc && Number(calc) > 0) return calc;
              if (patient.age && !isNaN(Number(patient.age)) && Number(patient.age) > 0) return Number(patient.age);
              return "";
            })();

            const visitTimestamp =
              patient.lastVisitDate ||
              patient.time ||
              patient.appointmentDate ||
              patient.updatedAt ||
              patient.lastVisit ||
              patient.createdAt;
            const lastVisit = visitTimestamp ? formatDate(visitTimestamp) : "";
            const lastVisitAgo = visitTimestamp ? getTimeAgo(visitTimestamp) : "";

            // Helper function to safely get value or return empty string
            const safeGet = (value) => value || "";

            const isAbhaLinked = Boolean(
              patient.isAbhaLinked === true ||
              patient.isabhalinked === true ||
              patient.isABHALinked === true ||
              String(patient.isAbhaLinked).toLowerCase() === "true" ||
              String(patient.isabhalinked).toLowerCase() === "true" ||
              String(patient.isABHALinked).toLowerCase() === "true" ||
              (patient.maskedAbha && String(patient.maskedAbha).trim() && patient.maskedAbha !== "N/A") ||
              (patient.ABHANumber && String(patient.ABHANumber).trim() && patient.ABHANumber !== "N/A") ||
              (patient.abhaNumber && String(patient.abhaNumber).trim() && patient.abhaNumber !== "N/A") ||
              (patient.formattedAbhaId && String(patient.formattedAbhaId).trim() && patient.formattedAbhaId !== "N/A") ||
              (patient.abhaaddress && String(patient.abhaaddress).trim()) ||
              (patient.abhaAddress && String(patient.abhaAddress).trim()) ||
              (patient.ABHAAddress && String(patient.ABHAAddress).trim())
            );

            const maskedAbha =
              patient.maskedAbha ||
              (patient.ABHANumber ? `**** ${String(patient.ABHANumber).slice(-4)}` : "") ||
              patient.formattedAbhaId ||
              (patient.abhaaddress || patient.abhaAddress || patient.ABHAAddress || "");

            return {
              id: patient._id || patient.id,
              _id: patient._id || patient.id,
              name: fullName,
              fullName: fullName,
              age: computedAge ? `${computedAge}yr` : "",
              calculatedAge: computedAge,
              dob: dobFormatted,
              isAbhaLinked,
              isabhalinked: isAbhaLinked,
              maskedAbha,
              abhaId: maskedAbha || (isAbhaLinked ? "Linked" : "N/A"),
              formattedAbhaId: maskedAbha || patient.ABHANumber || "",
              lastVisit,
              lastVisitAgo,
              opdId:
                safeGet(patient.opdId) || (patient._id || patient.id || "").slice(-8).toUpperCase(),
              queue:
                patient.queue !== undefined && patient.queue !== null
                  ? String(patient.queue).padStart(2, "0")
                  : "—",
              uhid:
                safeGet(patient.uhid || patient.UHID) ||
                (patient._id || patient.id || "").slice(-8).toUpperCase(),
              visitType: patient.visitType || "General OPD", // Added visitType
              fee:
                safeGet(
                  patient.fee !== undefined && patient.fee !== ""
                    ? (typeof patient.fee === "number" ? `₹${patient.fee}` : patient.fee)
                    : patient.consultationFee
                    ? `₹${patient.consultationFee}`
                    : "",
                ) || "",
              time: safeGet(patient.time || patient.appointmentTime || "") || "",
              date: formatDate(patient.lastVisitDate || patient.appointmentDate || patient.time || new Date()),
              doctor:
                safeGet(
                  patient.doctor ||
                  patient.consultingDoctor ||
                  patient.assignedDoctor ||
                  patient.lastVisitedDoctor ||
                  patient?.visits?.[0]?.doctor,
                ) || "",
              mobile: safeGet(patient.mobile),
              address: safeGet(patient.address),
              pincode: safeGet(patient.pincode),
              status: safeGet(patient.status),
              // Store all raw patient data
              rawData: {
                ...patient,
                name: fullName,
                fullName: fullName,
                calculatedAge: computedAge,
                age: computedAge ? `${computedAge}yr` : "",
                isAbhaLinked,
                isabhalinked: isAbhaLinked,
                maskedAbha,
                formattedAbhaId: maskedAbha || patient.ABHANumber || "",
              },
            };
          });

          setPatients(transformedPatients);
          setTotalPatients(response.data.total);
        } else {
          setError("Failed to fetch patients data");
        }
      } catch (err) {
        console.error("Error fetching patients:", err);
        setError(err.message || "Failed to fetch patients data");
      } finally {
        setLoading(false);
      }
    };

    fetchPatients();
  }, []);

  // Function to handle patient selection and navigate to discharge summary form (creates visit if none)
  const handlePatientSelect = async (patient) => {
    if (navigatingPatientId) return;

    if (patientHasNoVisits(patient)) {
      setPatientForVisit(patient);
      setShowVisitModal(true);
      return;
    }

    try {
      setNavigatingPatientId(patient.id ?? patient._id);
      const patientToUse = await ensurePatientHasVisit(patient);
      const effectivePatientId = patientToUse.id || patient.id || patient._id;
      navigate(`/discharge-summary-form?patientId=${encodeURIComponent(effectivePatientId)}`, {
        state: {
          patientData: patientToUse.rawData,
          patientId: effectivePatientId,
        },
      });
    } catch (err) {
      console.error("Failed to create initial visit", err);
      showError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to create visit. Please try again.",
      );
    } finally {
      setNavigatingPatientId(null);
    }
  };

  const handleVisitCreated = (result) => {
    const apiPatient = result.patientData ?? result;
    const existingRaw = patientForVisit?.rawData ?? patientForVisit;

    const mergedRawData = {
      ...existingRaw,
      ...apiPatient,
      visits: apiPatient.visits ?? existingRaw?.visits,
      visitId:
        apiPatient.visitId ??
        apiPatient.visits?.[0]?.visitId ??
        existingRaw?.visitId,
    };

    const effectivePatientId = mergedRawData._id || mergedRawData.id;
    navigate(`/discharge-summary-form?patientId=${encodeURIComponent(effectivePatientId)}`, {
      state: {
        patientData: mergedRawData,
        patientId: effectivePatientId,
      },
    });
  };

  return (
    <>
      <style>
        {`
                    .patient-card {
                        border: 1px solid #e0e0e0;
                        border-radius: 8px;
                        padding: 20px;
                        margin-bottom: 15px;
                        background-color: #fff;
                        transition: all 0.3s ease;
                        cursor: pointer;
                    }
                    .patient-card:hover {
                        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                        transform: translateY(-2px);
                    }
                    .patient-card-header {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-bottom: 15px;
                    }
                    .patient-name {
                        font-size: 18px;
                        font-weight: 600;
                        color: #0B1B8D;
                    }
                    .discharge-status {
                        padding: 5px 15px;
                        border-radius: 20px;
                        font-size: 12px;
                        font-weight: 500;
                    }
                    .status-pending {
                        background-color: #fff3cd;
                        color: #856404;
                    }
                    .status-completed {
                        background-color: #d4edda;
                        color: #155724;
                    }
                    .patient-card-body {
                        display: grid;
                        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                        gap: 15px;
                    }
                    .patient-info-item {
                        display: flex;
                        flex-direction: column;
                    }
                    .patient-info-label {
                        font-size: 12px;
                        color: #6c757d;
                        margin-bottom: 5px;
                    }
                    .patient-info-value {
                        font-size: 14px;
                        font-weight: 500;
                        color: #333;
                    }
                `}
      </style>
      <div className="container-fluid">
        <div className="opd-page">
          <div className="header-section-new">
            <div className="header-left">
              <ul className="nav nav-tabs-new">
                <li className="nav-item">
                  <span
                    className={`nav-link ${activeTab === "All" ? "active" : ""}`}
                    onClick={() => setActiveTab("All")}
                  >
                    All Patients
                  </span>
                </li>
                <li className="nav-item">
                  <span
                    className={`nav-link ${activeTab === "Pending" ? "active" : ""}`}
                    onClick={() => setActiveTab("Pending")}
                  >
                    Pending Discharge
                  </span>
                </li>
                <li className="nav-item">
                  <span
                    className={`nav-link ${activeTab === "Completed" ? "active" : ""}`}
                    onClick={() => setActiveTab("Completed")}
                  >
                    Completed
                  </span>
                </li>
              </ul>
            </div>
            <div className="header-right">
              <div className="search-bar-new">
                <span className="search-icon">
                  <img src={Search} alt="Search" />
                </span>
                <input
                  type="text"
                  placeholder="Search by Name, ID"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <button
                className="btn-refresh-new"
                onClick={() => window.location.reload()}
                disabled={loading}
              >
                {loading ? "Refreshing..." : "↻ Refresh"}
              </button>
            </div>
          </div>

          <div className="OpdBodySection">
            <div className="tab-content">
              <div id="All" className="tab-pane active">
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2 mb-0">Loading patients...</p>
                  </div>
                ) : error ? (
                  <div className="alert alert-danger">
                    <strong>Error:</strong> {error}
                    <br />
                    <button
                      className="btn btn-sm btn-outline-primary mt-2"
                      onClick={() => window.location.reload()}
                    >
                      Retry
                    </button>
                  </div>
                ) : patients.length === 0 ? (
                  <div className="alert alert-info">
                    <strong>No patients found.</strong>
                  </div>
                ) : (
                  patients
                    .filter((patient) => {
                      // Tab filtering
                      if (
                        activeTab === "Pending" &&
                        patient.status !== "Pending"
                      )
                        return false;
                      if (
                        activeTab === "Completed" &&
                        patient.status !== "Completed"
                      )
                        return false;

                      if (!searchTerm.trim()) return true;
                      const q = searchTerm.toLowerCase();
                      return (
                        patient.name?.toLowerCase().includes(q) ||
                        patient.mobile?.toLowerCase().includes(q) ||
                        patient.uhid?.toLowerCase().includes(q) ||
                        patient.opdId?.toLowerCase().includes(q) ||
                        patient.doctor?.toLowerCase().includes(q)
                      );
                    })
                    .map((patient, index) => (
                      <div key={patient.id || index} className="patient-card">
                        <div className="patient-card-header">
                          <div className="patient-name">{patient.name}</div>
                          <span className="discharge-status status-pending">
                            Pending
                          </span>
                        </div>
                        <div className="patient-card-body">
                          <div className="patient-info-item">
                            <span className="patient-info-label">OPD ID</span>
                            <span className="patient-info-value">
                              {patient.opdId}
                            </span>
                          </div>
                          <div className="patient-info-item">
                            <span className="patient-info-label">UHID</span>
                            <span className="patient-info-value">
                              {patient.uhid}
                            </span>
                          </div>
                          <div className="patient-info-item">
                            <span className="patient-info-label">Age</span>
                            <span className="patient-info-value">
                              {patient.age || "—"}{patient.dob ? ` | DOB: ${patient.dob}` : ""}
                            </span>
                          </div>
                          <div className="patient-info-item">
                            <span className="patient-info-label">Doctor</span>
                            <span className="patient-info-value">
                              {patient.doctor || "Not Assigned"}
                            </span>
                          </div>
                          <div className="patient-info-item">
                            <span className="patient-info-label">
                              Last Visit
                            </span>
                            <span className="patient-info-value">
                              {patient.lastVisit ? (
                                <>
                                  {patient.lastVisit}
                                  {patient.lastVisitAgo && ` (${patient.lastVisitAgo})`}
                                </>
                              ) : (
                                "—"
                              )}
                            </span>
                          </div>
                          <div className="patient-info-item">
                            <span className="patient-info-label">Mobile</span>
                            <span className="patient-info-value">
                              {patient.mobile || "N/A"}
                            </span>
                          </div>
                        </div>
                        <div className="mt-3">
                          <button
                            className="btn btn-primary btn-sm me-2"
                            disabled={!!navigatingPatientId}
                            onClick={() => handlePatientSelect(patient)}
                          >
                            {navigatingPatientId ===
                            (patient.id ?? patient._id) ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm me-1"
                                  role="status"
                                  aria-hidden="true"
                                />
                                Creating visit…
                              </>
                            ) : (
                              <>
                                <i
                                  className="material-icons"
                                  style={{
                                    fontSize: "16px",
                                    verticalAlign: "middle",
                                  }}
                                >
                                  assignment
                                </i>{" "}
                                Create Discharge Summary
                              </>
                            )}
                          </button>
                          {/* <button className="btn btn-outline-secondary btn-sm">
                            <i
                              className="material-icons"
                              style={{
                                fontSize: "16px",
                                verticalAlign: "middle",
                              }}
                            >
                              visibility
                            </i>{" "}
                            View Details
                          </button> */}
                        </div>
                      </div>
                    ))
                )}
                {!loading && !error && patients.length > 0 && (
                  <div className="mt-3 text-muted">
                    <small>Total Patients: {totalPatients}</small>
                  </div>
                )}
              </div>
              <div id="Pending" className="tab-pane fade">
                <div className="alert alert-info mt-3">
                  <p>Pending discharge summaries will appear here.</p>
                </div>
              </div>
              <div id="Completed" className="tab-pane fade">
                <div className="alert alert-success mt-3">
                  <p>Completed discharge summaries will appear here.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <CreateVisitModal
        isOpen={showVisitModal}
        onClose={() => setShowVisitModal(false)}
        patientId={patientForVisit?.id || patientForVisit?._id}
        patientName={patientForVisit?.name}
        patientMeta={
          patientForVisit
            ? `${patientForVisit.age || ""}${patientForVisit.dob ? ` / DOB: ${patientForVisit.dob}` : ""} / ${patientForVisit.mobile || ""} / ${patientForVisit.address || ""}`
                .replace(/\s*\/\s*\/\s*/g, " / ")
                .trim()
                .replace(/^\s*\/\s*/, "")
                .replace(/\s*\/\s*$/, "")
            : ""
        }
        onVisitCreated={handleVisitCreated}
      />
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default DischargeSummary;
